import React, { useState } from 'react';
import { FiTarget, FiClock, FiActivity, FiCheckCircle, FiUser, FiCalendar, FiTrendingUp, FiChevronDown, FiChevronRight } from 'react-icons/fi';

const ActionsTab = ({ insightData, Section }) => {
  const [immediateExpanded, setImmediateExpanded] = useState(true);
  const [shortTermExpanded, setShortTermExpanded] = useState(false);
  const [longTermExpanded, setLongTermExpanded] = useState(false);
  
  const onToggleImmediate = React.useCallback(() => setImmediateExpanded(v => !v), []);
  const onToggleShortTerm = React.useCallback(() => setShortTermExpanded(v => !v), []);
  const onToggleLongTerm = React.useCallback(() => setLongTermExpanded(v => !v), []);

  if (!insightData?.actionable_recommendations) return null;

  const actionableRecs = insightData.actionable_recommendations;

  return (
    <>
      {/* Actionable Recommendations */}
      <Section title="Actionable Recommendations" iconComponent={FiTarget}>
        <div className="space-y-4">
          {/* Immediate Actions */}
          {actionableRecs.immediate_actions && (
            <div className="bg-white rounded-2xl border border-gray-200 shadow-xl p-6 transition-all duration-200">
              <div 
                className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2 cursor-pointer select-none"
                onClick={onToggleImmediate}
              >
                <FiClock className="text-red-600" />
                Immediate Actions
                {immediateExpanded ? <FiChevronDown className="w-4 h-4" /> : <FiChevronRight className="w-4 h-4" />}
              </div>
              {immediateExpanded && (
                <div className="space-y-4">
                  {actionableRecs.immediate_actions.map((action, idx) => (
                    <div key={idx} className="bg-white rounded-2xl border border-gray-200 shadow-xl p-6 transition-all duration-200">
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-red-600 text-sm">⚡</span>
                        </div>
                        <div className="flex-1">
                          <div className="flex justify-between items-center mb-2">
                            <h4 className="text-sm font-semibold text-red-700 mb-2">
                              Priority {action.priority_score}
                            </h4>
                            <span className="text-xs text-gray-500">
                              Due: {action.deadline}
                            </span>
                          </div>
                          <div className="text-sm text-gray-700 leading-relaxed mb-3">
                            {action.action}
                          </div>
                          <div className="flex justify-between text-xs text-gray-500 mb-3">
                            <span><strong>Owner:</strong> {action.owner}</span>
                            <span><strong>Success Metric:</strong> {action.success_metric}</span>
                          </div>
                          {action.risk_if_delayed && (
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs text-red-800 mb-3">
                              <strong>Risk if delayed:</strong> {action.risk_if_delayed}
                            </div>
                          )}
                          {action.resources_required && (
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs text-blue-800">
                              <strong>Resources:</strong> {action.resources_required.join(', ')}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Short-term Initiatives */}
          {actionableRecs.short_term_initiatives && (
            <div className="bg-white rounded-2xl border border-gray-200 shadow-xl p-6 transition-all duration-200">
              <div 
                className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2 cursor-pointer select-none"
                onClick={onToggleShortTerm}
              >
                <FiActivity className="text-blue-600" />
                Short-term Initiatives
                {shortTermExpanded ? <FiChevronDown className="w-4 h-4" /> : <FiChevronRight className="w-4 h-4" />}
              </div>
              {shortTermExpanded && (
                <div className="space-y-4">
                  {actionableRecs.short_term_initiatives.map((initiative, idx) => (
                    <div key={idx} className="bg-white rounded-2xl border border-gray-200 shadow-xl p-6 transition-all duration-200">
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-blue-600 text-sm">📈</span>
                        </div>
                        <div className="flex-1">
                          <div className="flex justify-between items-center mb-2">
                            <h4 className="text-sm font-semibold text-gray-700 mb-2">
                              {initiative.initiative}
                            </h4>
                            <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-semibold">
                              {initiative.success_probability}% Success
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 mb-3">
                            Timeline: {initiative.timeline} • ROI: {initiative.roi_estimate}
                          </div>
                          <div className="text-xs text-gray-700 mb-3">
                            {initiative.expected_outcome}
                          </div>
                          {initiative.dependencies && (
                            <div className="text-xs text-gray-500 mb-3">
                              <strong>Dependencies:</strong> {initiative.dependencies.join(', ')}
                            </div>
                          )}
                          {initiative.resources_required && (
                            <div className="text-xs text-gray-500">
                              <strong>Resources:</strong> {initiative.resources_required.join(', ')}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Long-term Positioning */}
          {actionableRecs.long_term_positioning && (
            <div className="bg-white rounded-2xl border border-gray-200 shadow-xl p-6 transition-all duration-200">
              <div 
                className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2 cursor-pointer select-none"
                onClick={onToggleLongTerm}
              >
                <FiTrendingUp className="text-green-600" />
                Long-term Positioning
                {longTermExpanded ? <FiChevronDown className="w-4 h-4" /> : <FiChevronRight className="w-4 h-4" />}
              </div>
              {longTermExpanded && (
                <div className="space-y-4">
                  {actionableRecs.long_term_positioning.map((strategy, idx) => (
                    <div key={idx} className="bg-white rounded-2xl border border-gray-200 shadow-xl p-6 transition-all duration-200">
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-green-600 text-sm">🎯</span>
                        </div>
                        <div className="flex-1">
                          <div className="flex justify-between items-center mb-2">
                            <h4 className="text-sm font-semibold text-gray-700 mb-2">
                              {strategy.strategy}
                            </h4>
                            <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-semibold">
                              {strategy.expected_roi}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 mb-4">
                            Investment: {strategy.investment_level} • Timeline: {strategy.timeline}
                          </div>
                          
                          {strategy.milestones && (
                            <div>
                              <div className="text-xs font-semibold text-gray-700 mb-3">
                                Milestones:
                              </div>
                              {strategy.milestones.map((milestone, mIdx) => (
                                <div key={mIdx} className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-3">
                                  <div className="flex justify-between items-center mb-2">
                                    <div className="font-semibold text-gray-800 text-xs">
                                      {milestone.milestone}
                                    </div>
                                    <span className="text-xs text-green-600">
                                      {milestone.target_date}
                                    </span>
                                  </div>
                                  {milestone.success_criteria && (
                                    <div className="text-xs text-gray-500">
                                      <strong>Success:</strong> {milestone.success_criteria.join(', ')}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </Section>
    </>
  );
};

export default ActionsTab;
